import React, { useState } from 'react';
import '../../../components/project-group-header/ProjectGroupHeader.scss';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useTranslation } from 'react-i18next';
import { MonitorPromptGroup } from '../../../../../shared/models/monitor/MonitorPromptGroup';
import { useUpdatePromptGroup, useDeletePromptGroup } from '../../../../../shared/hooks/prompt/UsePromptGroups';
import { showCustomDialog } from '../../../../../shared/helpers/DialogHelper';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import { Tooltip } from 'primereact/tooltip';

interface PromptGroupHeaderProps {
  group: MonitorPromptGroup;
  promptCount: number;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  projectId: number;
  onDeleteGroup?: () => void;
  onDeleteSuccess?: () => void;
  readOnly?: boolean;
}

const PromptGroupHeader: React.FC<PromptGroupHeaderProps> = ({
  group,
  promptCount,
  isCollapsed,
  onToggleCollapse,
  projectId,
  onDeleteGroup,
  onDeleteSuccess,
  readOnly,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(group.name);

  const { mutate: updateGroup } = useUpdatePromptGroup(projectId);
  const { mutate: removeGroup } = useDeletePromptGroup(projectId, onDeleteSuccess);

  const handleSaveName = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== group.name) {
      updateGroup({ groupId: group.id, data: { name: trimmed } });
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSaveName();
    if (e.key === 'Escape') {
      setEditName(group.name);
      setIsEditing(false);
    }
  };

  const confirmDelete = () => {
    showCustomDialog({
      accept: () => {
        removeGroup(group.id);
        onDeleteGroup?.();
      },
    });
  };

  return (
    <div
      className={`project-group-header${isCollapsed ? ' project-group-header--collapsed' : ''}`}
      onClick={onToggleCollapse}
    >
      <div className="project-group-header__left">
        <span className="ico--up ico--xs project-group-header__chevron" />
        <span
          className="project-group-header__dot"
          style={{ backgroundColor: group.color || 'var(--grey-ux-400)' }}
        />
        {isEditing ? (
          <InputText
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleSaveName}
            onKeyDown={handleKeyDown}
            autoFocus
            className="project-group-header__input"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            <span id={`prompt-group-name-${group.id}`} className="project-group-header__name">{group.name}</span>
            <Tooltip target={`#prompt-group-name-${group.id}`} content={group.name} position="top" showDelay={300} />
          </>
        )}
        <span className="project-group-header__count">{promptCount}</span>
      </div>
      {!readOnly && (
        <div className="project-group-header__actions">
          <Button
            id={`btn-edit-prompt-group-${group.id}`}
            className="action-btn"
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
          >
            <span className="ico--edit ico--xs" />
          </Button>
          <CustomTooltip tooltipText="editPromptGroup" targetRef={`btn-edit-prompt-group-${group.id}`} />

          <Button
            id={`btn-remove-prompt-group-${group.id}`}
            className="action-btn"
            onClick={(e) => {
              e.stopPropagation();
              confirmDelete();
            }}
          >
            <span className="ico--trash ico--xs" />
          </Button>
          <CustomTooltip
            tooltipText="removePromptGroup"
            targetRef={`btn-remove-prompt-group-${group.id}`}
          />
        </div>
      )}
    </div>
  );
};

export default PromptGroupHeader;
